import { createContext, ReactNode, use, useEffect, useRef } from 'react'

import { Schemas } from '~/clients/api-client'
import env from '~/env'
import { useVersionQuery } from '~/hooks/tanstack-query/baseClient/version/useVersionQuery'
import { useFlag } from '~/hooks/useFlag'
import { useStore } from '~/store/store'
import { trackUserEvent } from '~/utils/analytics'

type VersionCheckContextValue = {
    apiVersion: Schemas['Version'] | undefined
    isOutdated: boolean
    isLoading: boolean
}

const VersionCheckContext = createContext<VersionCheckContextValue | null>(null)

/**
 * Parse a semantic version string into its components.
 * Handles formats like "v1.2.3", "1.2.3", "v1.2.3-beta.1"
 * Returns null for PR versions (e.g., "pr-123") since they can't be compared semantically
 */
const parseVersion = (version: string): { major: number; minor: number; patch: number } | null => {
    // PR versions (e.g., "pr-123") cannot be compared semantically
    if (/^pr-\d+$/i.test(version)) return null

    const match = version.replace(/^v/, '').match(/^(\d+)\.(\d+)\.(\d+)/)
    if (!match || !match[1] || !match[2] || !match[3]) return null
    return {
        major: parseInt(match[1], 10),
        minor: parseInt(match[2], 10),
        patch: parseInt(match[3], 10),
    }
}

/**
 * Compare two semantic versions.
 * Handles formats like "v1.2.3", "1.2.3", "v1.2.3-beta.1"
 * Returns: -1 if a < b, 0 if a === b, 1 if a > b, null if either version is unparseable
 */
const compareVersions = (a: string, b: string): number | null => {
    const parsedA = parseVersion(a)
    const parsedB = parseVersion(b)

    if (!parsedA || !parsedB) return null

    if (parsedA.major !== parsedB.major) return parsedA.major < parsedB.major ? -1 : 1
    if (parsedA.minor !== parsedB.minor) return parsedA.minor < parsedB.minor ? -1 : 1
    if (parsedA.patch !== parsedB.patch) return parsedA.patch < parsedB.patch ? -1 : 1

    return 0
}

/**
 * Check if the client version is behind (older than) the API version.
 * Only returns true if the client needs to update to a newer version.
 */
const checkClientBehind = (apiVersion: Schemas['Version'] | undefined): boolean => {
    if (!apiVersion) {
        console.info('[VersionCheck] No API version available yet')
        return false
    }

    const clientVersion = env.VITE_GIT_TAG
    const clientSha = env.VITE_GIT_SHA

    // If client version info is not available, we can't determine if behind
    if (!clientVersion) {
        console.info(`[VersionCheck] No client version info available (VITE_GIT_TAG is not set), client sha=${clientSha}`)
        return false
    }

    // If API version is not available, we can't compare
    if (!apiVersion.version) {
        console.info(`[VersionCheck] No API version tag available, client=${clientVersion} (${clientSha}), api sha=${apiVersion.sha}`)
        return false
    }

    const comparison = compareVersions(clientVersion, apiVersion.version)

    if (comparison === null) {
        console.info(
            `[VersionCheck] Unable to compare versions - unparseable format: client=${clientVersion} (${clientSha}), api=${apiVersion.version} (${apiVersion.sha})`
        )
        return false
    }

    // we only want to alert if the client is behind, not if it's ahead.
    // p.g.a. potentially running a newer client against an older API (e.g., during a gradual rollout or if the user has a pre-release version)
    // would cause an annoying toast that doesn't go away until the api is updated, even though the user is not actually affected by any issues.
    if (comparison < 0) {
        console.info(`[VersionCheck] Client is behind: client=${clientVersion} (${clientSha}), api=${apiVersion.version} (${apiVersion.sha})`)
        return true
    }

    if (comparison === 0) {
        console.info(`[VersionCheck] Versions are equal: client=${clientVersion} (${clientSha}), api=${apiVersion.version} (${apiVersion.sha})`)
        return false
    }

    console.info(`[VersionCheck] Client is ahead: client=${clientVersion} (${clientSha}), api=${apiVersion.version} (${apiVersion.sha})`)
    return false
}

const TOAST_SUPPRESSION_MS = 5 * 60 * 1000 // 5 minutes
const TOAST_DISMISSED_AT_KEY = 'hero_version_toast_dismissed_at'

const getSuppressionTimestamp = (): number | null => {
    const stored = localStorage.getItem(TOAST_DISMISSED_AT_KEY)
    return stored ? parseInt(stored, 10) : null
}

const setSuppressionTimestamp = (): void => {
    localStorage.setItem(TOAST_DISMISSED_AT_KEY, Date.now().toString())
}

export function VersionCheckProvider({ children }: { children: ReactNode }) {
    const isVersionCheckEnabled = useFlag('version_check__enabled')
    const { data: apiVersion, isLoading } = useVersionQuery({ enabled: isVersionCheckEnabled })
    const hasShownToastRef = useRef(false)
    const toastIdRef = useRef<string | null>(null)

    const isOutdated = isVersionCheckEnabled ? checkClientBehind(apiVersion) : false

    useEffect(() => {
        // Check if we're still in the suppression window after user dismissed the toast
        const dismissedAt = getSuppressionTimestamp()
        const isInSuppressionWindow = dismissedAt !== null && Date.now() - dismissedAt < TOAST_SUPPRESSION_MS

        if (isOutdated && !hasShownToastRef.current && !isInSuppressionWindow) {
            hasShownToastRef.current = true
            const toastId = crypto.randomUUID()
            toastIdRef.current = toastId
            let wasAccepted = false

            const timeoutId = setTimeout(() => {
                // useStore.getState() avoids useSyncExternalStore subscriptions, which would trigger a flushSync error during render
                useStore.getState().toaster.actions.addToast({
                    id: toastId,
                    color: 'info',
                    message: 'Ny versjon av Hero tilgjengelig!',
                    placement: 'top-center',
                    persist: true,
                    action: {
                        label: 'Oppdater',
                        onClick: () => {
                            wasAccepted = true
                            void trackUserEvent('VERSION_UPDATE_TOAST_ACCEPTED')
                            setSuppressionTimestamp()
                            window.location.reload()
                        },
                    },
                    onClose: () => {
                        hasShownToastRef.current = false
                        if (!wasAccepted) {
                            setSuppressionTimestamp()
                            void trackUserEvent('VERSION_UPDATE_TOAST_DISMISSED')
                        }
                    },
                })
                void trackUserEvent('VERSION_UPDATE_TOAST_SHOWN')
            }, 0)

            return () => clearTimeout(timeoutId)
        }

        // Reset toast tracking if version becomes current again (e.g., after a code push that reverts)
        if (!isOutdated && hasShownToastRef.current && toastIdRef.current) {
            const toastId = toastIdRef.current
            hasShownToastRef.current = false
            toastIdRef.current = null
            useStore.getState().toaster.actions.removeToast(toastId)
        }
    }, [isOutdated])

    const contextValues: VersionCheckContextValue = {
        apiVersion,
        isOutdated,
        isLoading,
    }

    return <VersionCheckContext value={contextValues}>{children}</VersionCheckContext>
}

export function useVersionCheck(): VersionCheckContextValue {
    const context = use(VersionCheckContext)
    if (!context) {
        throw new Error('useVersionCheck must be used within a VersionCheckProvider')
    }
    return context
}
